export interface LineMetricPoint {
  x?: string;
  bucket?: string;
  average?: number | null;
  avg?: number | null;
  min?: number | null;
  max?: number | null;
  sum?: number | null;
  count?: number | null;
  median?: number | null;
  variance?: number | null;
  stddev?: number | null;
  unit?: string | null;
  areaId?: number | null;
}

export interface LineMetricSeries {
  metricKey: string;
  aggregations?: string[];
  points?: LineMetricPoint[];
}

const FIELD_LABELS: Record<string, string> = {
  average: 'Average',
  min: 'Min',
  max: 'Max',
  count: 'Count',
  sum: 'Sum',
  median: 'Median',
  variance: 'Variance',
  stddev: 'Std Dev',
};

const normalizeField = (field: string) => (field === 'avg' ? 'average' : field);

const fieldOrder = ['average', 'min', 'max', 'count', 'sum', 'median', 'variance', 'stddev'] as const;
const sortFields = (fields: string[]) => {
  const unique = Array.from(new Set(fields));
  const ordered = fieldOrder.filter((f) => unique.includes(f));
  const rest = unique.filter((f) => !fieldOrder.includes(f as any));
  return [...ordered, ...rest];
};

const formatAggregationLabel = (field: string) => {
  const normalized = normalizeField(field);
  return FIELD_LABELS[normalized] || normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

type XScaleType = 'time' | 'linear' | 'cat';

type XCandidate =
  | { kind: 'time'; ms: number }
  | { kind: 'linear'; n: number }
  | { kind: 'cat'; s: string }
  | { kind: 'null' };

function parseXCandidate(input: unknown): XCandidate {
  if (input === null || input === undefined) return { kind: 'null' };

  if (input instanceof Date) {
    const ms = input.getTime();
    return Number.isFinite(ms) ? { kind: 'time', ms } : { kind: 'null' };
  }

  if (typeof input === 'number') {
    return Number.isFinite(input) ? { kind: 'linear', n: input } : { kind: 'null' };
  }

  const s = String(input).trim();
  if (!s) return { kind: 'null' };

  const ms = Date.parse(s);
  if (!Number.isNaN(ms)) return { kind: 'time', ms };

  const n = Number(s);
  if (Number.isFinite(n)) return { kind: 'linear', n };

  return { kind: 'cat', s };
}

function chooseXScaleType(candidates: XCandidate[]): XScaleType {
  const nonNull = candidates.filter((c) => c.kind !== 'null');
  if (nonNull.length === 0) return 'time';

  const allTime = nonNull.every((c) => c.kind === 'time');
  if (allTime) return 'time';

  const allLinear = nonNull.every((c) => c.kind === 'linear');
  if (allLinear) return 'linear';

  return 'cat';
}

function coerceXToScale(
  input: unknown,
  scaleType: XScaleType
): { x: Date | number | string; sortKey: number | string } | null {
  const parsed = parseXCandidate(input);
  if (parsed.kind === 'null') return null;

  if (scaleType === 'time') {
    if (parsed.kind !== 'time') return null;
    const d = new Date(parsed.ms);
    return { x: d, sortKey: parsed.ms };
  }

  if (scaleType === 'linear') {
    if (parsed.kind !== 'linear') return null;
    return { x: parsed.n, sortKey: parsed.n };
  }

  if (parsed.kind === 'time') {
    const iso = new Date(parsed.ms).toISOString();
    return { x: iso, sortKey: iso };
  }
  if (parsed.kind === 'linear') {
    const s = String(parsed.n);
    return { x: s, sortKey: s };
  }
  return { x: parsed.s, sortKey: parsed.s };
}

export type PlotPoint = {
  x: Date | number | string;
  value: number;
  series: string;
  __seriesKey: string;
  __sortKey: number | string;
  __isParent?: boolean;
};

export type PlotBand = {
  x: Date | number | string;
  min: number;
  max: number;
  bandSeries?: string;
  __sortKey: number | string;
};

export interface LineChartDataResult {
  displayPoints: PlotPoint[];
  displayBands?: PlotBand[];
  unitLabel?: string;
  xScaleType: XScaleType;
  showPlaceholder: boolean;
}

interface BuildParams {
  data?: LineMetricSeries[];
  fields?: string[];
  metricLabels?: Record<string, string>;
  areaNames?: Record<number, string>;
  showParent?: boolean;
  showChildren?: boolean;
  previewFallback?: boolean;
  fallbackMetricKey?: string;
}

export function buildLineChartData(params: BuildParams): LineChartDataResult {
  const {
    data,
    fields,
    metricLabels,
    areaNames,
    showParent = true,
    showChildren = false,
    previewFallback,
    fallbackMetricKey,
  } = params;

  const lineData = Array.isArray(data) ? data : [];
  const selectedFields = Array.isArray(fields) ? fields.map((f) => normalizeField(f)) : [];
  const showMetricLabel = lineData.length > 1;

  const unitValues = new Set<string>();
  lineData.forEach((metricEntry) => {
    (metricEntry.points ?? []).forEach((p) => {
      if (p.unit) unitValues.add(p.unit);
    });
  });
  const resolvedUnitLabel = unitValues.size === 1 ? Array.from(unitValues)[0] : undefined;

  const xCandidates: XCandidate[] = [];
  lineData.forEach((metricEntry) => {
    (metricEntry.points ?? []).forEach((p) => {
      const rawX = p.x ?? p.bucket;
      xCandidates.push(parseXCandidate(rawX));
    });
  });

  const resolvedXScaleType = chooseXScaleType(xCandidates);

  const points: PlotPoint[] = [];
  const bands: PlotBand[] = [];

  lineData.forEach((metricEntry) => {
    const metricKey = metricEntry.metricKey || fallbackMetricKey || 'metric';
    const metricLabel = metricLabels?.[metricKey] || metricKey;

    const pointList = Array.isArray(metricEntry.points) ? metricEntry.points : [];

    const availableFields = new Set<string>();
    if (Array.isArray(metricEntry.aggregations) && metricEntry.aggregations.length > 0) {
      metricEntry.aggregations.forEach((agg) => availableFields.add(normalizeField(agg)));
    } else if (pointList.length > 0) {
      ['average', 'avg', 'min', 'max', 'count', 'sum', 'median', 'variance', 'stddev'].forEach((field) => {
        if (
          pointList.some(
            (p) =>
              (p as any)[field] !== undefined &&
              (p as any)[field] !== null
          )
        ) {
          availableFields.add(normalizeField(field));
        }
      });
    }

    const resolvedFields =
      selectedFields.length > 0
        ? availableFields.size > 0
          ? selectedFields.filter((f) => availableFields.has(f))
          : selectedFields
        : Array.from(availableFields);

    const fieldsToUse = sortFields(resolvedFields.length > 0 ? resolvedFields : ['average']);
    const hasBandFields = ['average', 'min', 'max'].every((f) => fieldsToUse.includes(f));

    type AggregatedPoint = {
      x: any;
      areaId?: number;
      unit?: string | null;
      values: { [field: string]: number[] };
      isParent?: boolean;
    };

    const aggregationMap = new Map<string, AggregatedPoint>();

    if (showParent) {
      pointList.forEach((p: any) => {
        const rawX = p.x ?? p.bucket;
        const xKey = String(rawX || '');
        const mapKey = `parent-${xKey}`;

        if (!aggregationMap.has(mapKey)) {
          aggregationMap.set(mapKey, {
            x: rawX,
            areaId: undefined,
            unit: p.unit,
            values: {},
            isParent: true,
          });
        }

        const entry = aggregationMap.get(mapKey)!;

        fieldsToUse.forEach((field) => {
          const rawValue = field === 'average' ? p.average ?? p.avg : (p as any)[field];
          if (rawValue !== null && rawValue !== undefined) {
            if (!entry.values[field]) entry.values[field] = [];
            const num = typeof rawValue === 'number' ? rawValue : Number(rawValue);
            if (Number.isFinite(num)) {
              entry.values[field].push(num);
            }
          }
        });
      });
    }

    if (showChildren) {
      pointList.forEach((p: any) => {
        const rawX = p.x ?? p.bucket;
        const xKey = String(rawX || '');
        const areaId = p.areaId;

        if (areaId === null || areaId === undefined) return;

        const mapKey = `child-${xKey}-area${areaId}`;

        if (!aggregationMap.has(mapKey)) {
          aggregationMap.set(mapKey, {
            x: rawX,
            areaId,
            unit: p.unit,
            values: {},
            isParent: false,
          });
        }

        const entry = aggregationMap.get(mapKey)!;

        fieldsToUse.forEach((field) => {
          const rawValue = field === 'average' ? p.average ?? p.avg : (p as any)[field];
          if (rawValue !== null && rawValue !== undefined) {
            if (!entry.values[field]) entry.values[field] = [];
            const num = typeof rawValue === 'number' ? rawValue : Number(rawValue);
            if (Number.isFinite(num)) {
              entry.values[field].push(num);
            }
          }
        });
      });
    }

    const processedPoints = Array.from(aggregationMap.values()).map((entry) => {
      const result: any = {
        x: entry.x,
        bucket: entry.x,
        areaId: entry.areaId,
        unit: entry.unit,
        isParent: entry.isParent,
      };

      Object.keys(entry.values).forEach((field) => {
        const values = entry.values[field];
        if (values.length === 0) return;

        if (field === 'average' || field === 'median') {
          result[field] = values.reduce((a, b) => a + b, 0) / values.length;
        } else if (field === 'min') {
          result[field] = Math.min(...values);
        } else if (field === 'max') {
          result[field] = Math.max(...values);
        } else if (field === 'sum' || field === 'count') {
          result[field] = values.reduce((a, b) => a + b, 0);
        } else if (field === 'variance' || field === 'stddev') {
          result[field] = values.reduce((a, b) => a + b, 0) / values.length;
        }
      });

      return result;
    });

    processedPoints.forEach((p) => {
      const rawX = p.x ?? p.bucket;
      const coerced = coerceXToScale(rawX, resolvedXScaleType);
      if (!coerced) return;

      const { x, sortKey } = coerced;

      fieldsToUse.forEach((field) => {
        const rawValue = field === 'average' ? p.average ?? p.avg : (p as any)[field];
        if (rawValue === null || rawValue === undefined) return;

        const value = typeof rawValue === 'number' ? rawValue : Number(rawValue);
        if (!Number.isFinite(value)) return;

        const fieldLabel = formatAggregationLabel(field);

        let seriesLabel: string;
        if (p.isParent) {
          seriesLabel = showMetricLabel ? `${metricLabel} - ${fieldLabel}` : fieldLabel;
        } else if (p.areaId !== null && p.areaId !== undefined) {
          const areaLabel = areaNames?.[p.areaId] || `Area ${p.areaId}`;
          if (showMetricLabel) {
            seriesLabel = `${metricLabel} - ${areaLabel} - ${fieldLabel}`;
          } else {
            seriesLabel = `${areaLabel} - ${fieldLabel}`;
          }
        } else {
          seriesLabel = showMetricLabel ? `${metricLabel} - ${fieldLabel}` : fieldLabel;
        }

        points.push({
          x,
          value,
          series: seriesLabel,
          __seriesKey: seriesLabel,
          __sortKey: sortKey,
          __isParent: p.isParent,
        });
      });

      if (hasBandFields && showParent) {
        const minRaw = p.min;
        const maxRaw = p.max;
        if (minRaw === null || minRaw === undefined || maxRaw === null || maxRaw === undefined) return;

        const min = typeof minRaw === 'number' ? minRaw : Number(minRaw);
        const max = typeof maxRaw === 'number' ? maxRaw : Number(maxRaw);
        if (!Number.isFinite(min) || !Number.isFinite(max)) return;

        bands.push({
          x,
          min,
          max,
          bandSeries: metricLabel,
          __sortKey: sortKey,
        });
      }
    });
  });

  const bySeries = new Map<string, PlotPoint[]>();
  points.forEach((p) => {
    if (!bySeries.has(p.__seriesKey)) bySeries.set(p.__seriesKey, []);
    bySeries.get(p.__seriesKey)!.push(p);
  });

  const orderedPoints = Array.from(bySeries.values()).flatMap((seriesPts) =>
    [...seriesPts].sort((a, b) => {
      const ak = a.__sortKey;
      const bk = b.__sortKey;
      if (typeof ak === 'number' && typeof bk === 'number') return ak - bk;
      return String(ak).localeCompare(String(bk));
    })
  );

  const orderedBands = [...bands].sort((a, b) => {
    const ak = a.__sortKey;
    const bk = b.__sortKey;
    if (typeof ak === 'number' && typeof bk === 'number') return ak - bk;
    return String(ak).localeCompare(String(bk));
  });

  const fallbackPoints: PlotPoint[] = [
    { x: new Date('2025-01-01'), value: 75, series: 'Average', __seriesKey: 'Average', __sortKey: Date.parse('2025-01-01') },
    { x: new Date('2025-01-02'), value: 78, series: 'Average', __seriesKey: 'Average', __sortKey: Date.parse('2025-01-02') },
    { x: new Date('2025-01-03'), value: 82, series: 'Average', __seriesKey: 'Average', __sortKey: Date.parse('2025-01-03') },
  ];

  const showPlaceholder = !!previewFallback && orderedPoints.length === 0;
  const displayPoints = showPlaceholder ? fallbackPoints : orderedPoints;
  const displayBands = displayPoints.length && orderedBands.length ? orderedBands : undefined;

  return {
    displayPoints,
    displayBands,
    unitLabel: resolvedUnitLabel,
    xScaleType: showPlaceholder ? 'time' : resolvedXScaleType,
    showPlaceholder,
  };
}
